import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import {
  createBlobURL,
  downloadFile,
  getFileUrl,
  useBlobURL,
  useDownloadStatus,
  useUploadStatus,
} from '@weblite/file'
import { Profiler } from '@weblite/profiler'
import { useSeekValue } from '@weblite/audio'
import { CategoryType, RawFileMessage } from '@weblite/ts-action/message'
import { ActionHandlerState } from '$common/action-handler'
import { isNumber, nothing } from '$func/utils'
import { deletePendingUpload } from '$logic/pending-uploads'
import { useAutoDownload } from '$logic/client-device'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { getNextAndPrevMessagesByType } from '$logic/shared-media/service'
import { deletePendingEditUpload } from '$logic/pending-edit-uploads'
import { ProfilerAction } from '$logic/profiler'
import { useConnectionStatus } from '$comp/main/react-hook'
import {
  DownloadFailedSource,
  reportDownloadFailure,
} from '$logic/analytics/failure-reporters/download-failed'

type MessageType = 'FILE' | 'AUDIO' | 'VOICE' | 'IMAGE' | 'VIDEO'

export const createBlobCreator = (messageType: MessageType) => {
  return (file: BlobPart[] | Blob | any) => {
    if (!file) return null

    if (messageType === 'FILE') return new Blob([])
    if (messageType === 'AUDIO') return new Blob([file], { type: 'audio/mpeg' })
    if (messageType === 'VOICE') return new Blob([file], { type: 'audio/mpeg' })
    if (messageType === 'IMAGE') return file
    if (messageType === 'VIDEO') return file

    return null
  }
}

export function useMessageFileUrl(fileId: string, messageType: MessageType) {
  return useBlobURL({
    fileId,
    fileUrl: getFileUrl(fileId),
    blobCreator: createBlobCreator(messageType),
  })
}

const DOWNLOAD_FAIL_TIMEOUT = 10 * 1000
export function useContentFileStatus({
  fileId,
  uploadId,
  messageType,
  hasConversion,
  messageId,
  filename,
}: {
  fileId: string
  uploadId: string
  messageType: MessageType
  hasConversion?: boolean
  messageId?: string
  filename?: string
}) {
  const blobURL = useMessageFileUrl(fileId, messageType)

  const downloadProfilerRef = useRef(new Profiler(ProfilerAction.DOWNLOAD))

  const downloadFailTimeoutRef = useRef<NodeJS.Timeout>()
  const connectionStatus = useConnectionStatus()

  const canTryToDownload = useRef(true)
  const {
    uploading,
    sentBytes: uploadedBytes,
    percentage: uploadPercentage,
    conversionPercentage,
    cancelUpload,
  } = useUploadStatus(uploadId)

  const {
    downloading,
    percentage: downloadPercentage,
    cancelDownload,
  } = useDownloadStatus(fileId)

  const startDownload = useCallback(
    () =>
      new Promise((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        downloadProfilerRef.current.start()
        if (connectionStatus === 'connected') {
          downloadFailTimeoutRef.current = setTimeout(() => {
            reportDownloadFailure(
              DownloadFailedSource.CHAT,
              messageType,
              DOWNLOAD_FAIL_TIMEOUT,
            )
          }, DOWNLOAD_FAIL_TIMEOUT)
        }
        downloadFile({
          fileId,
          url: getFileUrl(fileId),
          onProgress: () => {
            if (downloadFailTimeoutRef.current) {
              clearTimeout(downloadFailTimeoutRef.current)
              downloadFailTimeoutRef.current = undefined
            }
          },

          onComplete: (file: BlobPart[]) => {
            // TODO: get file size from @weblite/storage
            const blob = new Blob(Array.isArray(file) ? file : [file])
            const durationSeconds =
              downloadProfilerRef.current.getTotalDuration() / 1000

            const sizeInMegabyte = blob.size / (1024 * 1024)
            const downloadSpeed = sizeInMegabyte / durationSeconds

            downloadProfilerRef.current.stop({
              downloadSpeedMBPerSecond: downloadSpeed,
              fileSizeMB: sizeInMegabyte,
              messageType: messageType,
            })

            createBlobURL(fileId, createBlobCreator(messageType)(file))
            resolve(fileId)
          },
          onError: reject,
          fileName: filename,
        })
      }),
    [fileId, messageType, connectionStatus, filename],
  )

  const isAutoDownloadEnabled = useAutoDownload()

  const state: ActionHandlerState =
    isNumber(conversionPercentage) ||
    (uploadPercentage === 100 && hasConversion)
      ? 'CONVERTING'
      : uploading
        ? 'UPLOADING'
        : downloading
          ? 'DOWNLOADING'
          : blobURL
            ? 'AVAILABLE'
            : 'NOT_AVAILABLE'

  useEffect(() => {
    if (!canTryToDownload.current) return
    if (isAutoDownloadEnabled && state === 'NOT_AVAILABLE') {
      startDownload()
      canTryToDownload.current = false
    }
  }, [isAutoDownloadEnabled, state])

  const cancelOperation = useCallback(() => {
    const func =
      state === 'DOWNLOADING'
        ? () => {
            canTryToDownload.current = false
            cancelDownload()
          }
        : state === 'UPLOADING' || state === 'CONVERTING'
          ? () => {
              deletePendingUpload(uploadId)
              deletePendingEditUpload(uploadId, messageId)
            }
          : nothing

    func()
  }, [state, uploadId, cancelDownload])

  return {
    state,
    percentage:
      state === 'UPLOADING' || state === 'CONVERTING'
        ? uploadPercentage
        : state === 'DOWNLOADING'
          ? downloadPercentage
          : null,
    conversionPercentage: conversionPercentage || 0,
    uploadedBytes,
    blobURL,
    startDownload,
    cancelOperation,
  }
}

export function useGetPrevAndNextByType(
  roomId: string,
  iid: number,
  category: CategoryType,
) {
  const { value, loading } = useAsyncFunction(
    () => getNextAndPrevMessagesByType(roomId, iid, category),
    [roomId, iid],
  )
  return { ...value, loading }
}

export function useSeek({
  src,
  canSeek,
  containerRef,
  seekContainerRef,
}: {
  src: string
  canSeek: boolean
  initialSeek?: number
  containerRef: RefObject<HTMLDivElement>
  seekContainerRef: RefObject<HTMLDivElement>
}) {
  const [seeking, setSeeking] = useState(false)
  const [seekValue, setSeekValue] = useSeekValue(src)

  const calculateSeek = useCallback(
    (mouseClientX: number) => {
      if (!canSeek) return 0

      const seekContainerEl = seekContainerRef.current
      if (!seekContainerEl) return 0

      const { x: seekContainerLeftEdge } =
        seekContainerEl.getBoundingClientRect()

      const thumbOffset = mouseClientX - seekContainerLeftEdge
      const seek = thumbOffset / seekContainerEl.offsetWidth

      if (seek < 0) return 0
      if (seek > 1) return 1
      return seek
    },
    [canSeek],
  )

  useEffect(() => {
    const containerEl = containerRef.current
    const seekContainerEl = seekContainerRef.current

    if (!containerEl || !seekContainerEl) return

    seekContainerEl.onmousedown = ({ clientX }: { clientX: number }) => {
      setSeeking(true)
      setSeekValue(calculateSeek(clientX))
    }

    containerEl.onmouseleave = () => {
      if (seeking) setSeeking(false)
    }

    containerEl.onmouseup = () => {
      if (!seeking) return
      setSeeking(false)
    }

    containerEl.onmousemove = ({ clientX }: { clientX: number }) => {
      if (!seeking) return
      setSeekValue(calculateSeek(clientX))
    }
  }, [seeking])

  return seekValue
}
